import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toggle } from '../ui/FormControls';

const Segmented = ({ options, value, onSelect, disabled }) => (
    <div className="flex gap-1 bg-muted/40 rounded-lg p-1">
        {options.map(o => (
            <button key={o.value} type="button" disabled={disabled} onClick={() => onSelect(o.value)}
                aria-pressed={value === o.value}
                className={`flex-1 rounded-md px-2 py-1.5 text-sm font-medium transition-colors disabled:opacity-60 ${value === o.value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}>
                {o.label}
            </button>
        ))}
    </div>
);

function VolumeSlider({ value, disabled, onCommit }) {
    const [draft, setDraft] = useState(value);
    const commit = () => { if (draft !== value) onCommit(draft); };
    return (
        <div className="flex items-center gap-3">
            <input type="range" min={0} max={100} step={10} value={draft} disabled={disabled}
                onChange={e => setDraft(Number(e.target.value))}
                onPointerUp={commit} onKeyUp={commit}
                className="flex-1 accent-primary disabled:opacity-60" />
            <span className="w-10 text-right tabular-nums text-sm text-muted-foreground">{draft}</span>
        </div>
    );
}

const Row = ({ label, children }) => (
    <div className="px-4 py-3">
        <div className="text-sm mb-2">{label}</div>
        {children}
    </div>
);

// Only settings whose DPS is reported by the robot are rendered (the service sends just those).
export function SettingsCard({ settings, suction, canOperate, busy, onSet, onSuction }) {
    const { t } = useTranslation();
    const has = (k) => settings?.[k] !== undefined;
    const off = busy || !canOperate;
    if (suction === undefined && (!settings || Object.keys(settings).length === 0)) return null;

    const toggle = (key) => (
        <div className="px-4 py-3">
            <Toggle label={t(`maison.settings.${key}`)} checked={!!settings[key]} disabled={off}
                onChange={v => onSet(key, v)} />
        </div>
    );

    return (
        <section className="mb-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t('maison.settings.title')}</h3>
            <div className="bg-card border border-border rounded-xl divide-y divide-border">
                {suction !== undefined && (
                    <Row label={t('maison.suction')}>
                        <Segmented disabled={off} value={suction} onSelect={onSuction}
                            options={['gentle', 'normal', 'strong'].map(v => ({ value: v, label: t(`maison.${v}`) }))} />
                    </Row>
                )}
                {has('cistern') && (
                    <Row label={t('maison.settings.cistern')}>
                        <Segmented disabled={off} value={settings.cistern} onSelect={v => onSet('cistern', v)}
                            options={['low', 'middle', 'high'].map(v => ({ value: v, label: t(`maison.levels.${v}`) }))} />
                    </Row>
                )}
                {has('dust_collection_switch') && toggle('dust_collection_switch')}
                {has('dust_collection_num') && (
                    <Row label={t('maison.settings.dust_collection_num')}>
                        <Segmented disabled={off} value={settings.dust_collection_num} onSelect={v => onSet('dust_collection_num', v)}
                            options={[0, 1, 2, 3, 4].map(v => ({ value: v, label: String(v) }))} />
                    </Row>
                )}
                {has('volume_set') && (
                    <Row label={t('maison.settings.volume_set')}>
                        <VolumeSlider key={settings.volume_set} value={settings.volume_set} disabled={off}
                            onCommit={v => onSet('volume_set', v)} />
                    </Row>
                )}
                {has('break_clean') && toggle('break_clean')}
                {has('switch_disturb') && toggle('switch_disturb')}
            </div>
        </section>
    );
}
